<template>
    <div class="numbering-form">
        <!-- Method -->
        <panel-competition-form-box class="numbering-form__box mab16">
            <div class="col-12 col-lg-8">
                <p class="form-input__label">Metoda nadawania numerów startowych</p>
                <panel-input-base-radio
                    v-model="form.autoNumbers"
                    name="autoNumbers"
                    :option="{ value: 'auto', title: 'Automatyczna' }"
                />
                <div class="numbering-form__nested" :class="{ 'numbering-form__nested--disabled': !isAuto }">
                    <panel-input-checkbox
                        v-model="form.autoNumbersEmail"
                        :checkbox-value="true"
                        name="autoNumbersEmail"
                        variant="white"
                        :value="false"
                    >
                        Wysyłaj numer startowy w automatycznej wiadomości z potwierdzeniem rejestracji
                    </panel-input-checkbox>
                </div>
                <panel-input-base-radio
                    v-model="form.autoNumbers"
                    name="autoNumbers"
                    :option="{ value: 'manual', title: 'Ręczna' }"
                />
                <div class="numbering-form__nested" :class="{ 'numbering-form__nested--disabled': isAuto }">
                    <panel-input-base-radio
                        v-model="form.numbersOrder"
                        name="numbersOrder"
                        :option="{ value: 'date', title: 'Według daty rejestracji / opłaty' }"
                    />
                    <panel-input-base-radio
                        v-model="form.numbersOrder"
                        name="numbersOrder"
                        :option="{ value: 'alphabetic', title: 'Alfabetycznie' }"
                    />
                </div>
            </div>
            <div class="col-12 col-lg-4">
                <p class="form-input__hint">
                    Automatyczne nadawanie numerów startowych odbywa się w momencie rejestracji lub opłaty za
                    rejestrację, przez uczestnika.
                    <br /><br />
                    Ręczne nadanie numerów pozwala przypisać numery alfabetycznie wg nazwisk uczestników.
                </p>
            </div>
        </panel-competition-form-box>

        <!-- Paid classifications -->
        <panel-competition-form-box class="numbering-form__box mab16">
            <div class="col-12 col-lg-8">
                <p class="form-input__label">Nadawanie numerów w klasyfikacjach płatnych</p>
                <panel-input-base-radio
                    v-model="form.numbersOnlyPayed"
                    name="numbersOnlyPayed"
                    :option="{ value: 'all', title: 'Wszystkim zarejestrowanym' }"
                />
                <panel-input-base-radio
                    v-model="form.numbersOnlyPayed"
                    name="numbersOnlyPayed"
                    :option="{ value: 'paid', title: 'Tylko opłaconym' }"
                />
            </div>
            <div class="col-12 col-lg-4">
                <p class="form-input__hint">
                    Numery mogą być nadawane wszystkim zarejestrowanym uczestnikom lub tylko opłaconym. Uczestnicy w
                    klasyfikacjach bezpłatnych traktowani są jak opłaceni.
                </p>
            </div>
        </panel-competition-form-box>

        <!-- Cycle -->
        <panel-competition-form-box v-if="hasCycle" class="numbering-form__box mab16">
            <div class="col-12 col-lg-8">
                <p class="form-input__label">Zawody cykliczne</p>
                <panel-input-base-radio
                    v-model="form.numbersUsePrevious"
                    name="numbersUsePrevious"
                    :option="{
                        value: 'previous',
                        title: 'Nadawaj uczestnikom ich numery z poprzednich zawodów w tym cyklu',
                    }"
                />
                <div
                    class="numbering-form__nested"
                    :class="{ 'numbering-form__nested--disabled': form.numbersUsePrevious !== 'previous' }"
                >
                    <panel-input-checkbox
                        v-model="form.numbersOnlyPrevious"
                        :checkbox-value="true"
                        name="numbersOnlyPrevious"
                        variant="white"
                        :value="false"
                    >
                        Pomiń zawodników bez numeru
                    </panel-input-checkbox>
                </div>
                <panel-input-base-radio
                    v-model="form.numbersUsePrevious"
                    name="numbersUsePrevious"
                    :option="{ value: 'new', title: 'Nadawaj numery na nowo' }"
                />
            </div>
            <div class="col-12 col-lg-4">
                <p class="form-input__hint">
                    Uczestnicy mogą otrzymać ten sam numer, którym posługiwali się w poprzednich zawodach z tego samego
                    cyklu.
                </p>
            </div>
        </panel-competition-form-box>

        <!-- Existing numbers -->
        <panel-competition-form-box class="numbering-form__box mab16">
            <div class="col-12 col-lg-8">
                <p class="form-input__label">Istniejące numery</p>
                <panel-input-base-radio
                    v-model="form.numbersKeep"
                    name="numbersKeep"
                    :option="{ value: 'keep', title: 'Zachowaj' }"
                />
                <panel-input-base-radio
                    v-model="form.numbersKeep"
                    name="numbersKeep"
                    :option="{ value: 'reassign', title: 'Nadaj na nowo' }"
                />
            </div>
            <div class="col-12 col-lg-4">
                <p class="form-input__hint">
                    Numery mogą być nadawane wszystkim uczestnikom lub tylko uczestnikom bez numeru.
                </p>
            </div>
        </panel-competition-form-box>

        <!-- Parameters / pools -->
        <panel-competition-form-box class="numbering-form__box numbering-form__box--params mab16">
            <div class="col-12 col-lg-8 numbering-form__params-main">
                <p class="form-input__label">Parametry nadawania numerów</p>
                <div class="numbering-form__params-body">
                    <div class="numbering-form__presets-block">
                        <p class="numbering-form__preset-label">Wybierz z predefiniowanych parametrów</p>
                        <div class="numbering-form__presets">
                            <base-button
                                v-for="preset in presets"
                                :key="preset.id"
                                short
                                class="numbering-form__preset"
                                :class="{ 'numbering-form__preset--active': activePreset === preset.id }"
                                @click="applyPreset(preset.id)"
                            >
                                {{ preset.label }}
                            </base-button>
                        </div>
                    </div>

                    <div class="numbering-form__table">
                        <div class="numbering-form__table-head">
                            <div class="numbering-form__col-name-head">Klasyfikacja</div>
                            <div class="numbering-form__col-ranges">
                                <div class="numbering-form__col-from">Od nr</div>
                                <div class="numbering-form__col-sep numbering-form__col-sep--head" />
                                <div class="numbering-form__col-to">Do nr</div>
                            </div>
                        </div>

                        <div
                            v-for="(row, index) in form.classifications"
                            :key="row.id"
                            class="numbering-form__row"
                            :class="{ 'numbering-form__row--disabled': !row.enabled }"
                        >
                            <div class="numbering-form__row-main">
                                <label class="numbering-form__check">
                                    <input
                                        :checked="row.enabled"
                                        type="checkbox"
                                        @change="toggleClassification(index, $event.target.checked)"
                                    />
                                    <span class="numbering-form__check-box" />
                                </label>
                                <div class="numbering-form__col-body">
                                    <div class="numbering-form__col-name">
                                        <p class="numbering-form__cls-name">{{ row.name }}</p>
                                        <p class="numbering-form__cls-meta">
                                            <i class="fa fa-user" aria-hidden="true" />
                                            <span>{{ row.playersTotal }}</span>
                                        </p>
                                    </div>
                                    <div v-if="row.playersLimit > 0" class="numbering-form__col-limit">
                                        <p>Limit uczestników</p>
                                        <p>{{ row.playersLimit }}</p>
                                    </div>
                                    <div class="numbering-form__col-ranges">
                                        <div class="numbering-form__col-from">
                                            <input
                                                v-model.number="row.numbersMin"
                                                type="number"
                                                min="0"
                                                class="numbering-form__number-input"
                                                :disabled="!row.enabled"
                                            />
                                        </div>
                                        <div class="numbering-form__col-sep">…</div>
                                        <div class="numbering-form__col-to">
                                            <input
                                                v-model.number="row.numbersMax"
                                                type="number"
                                                min="0"
                                                class="numbering-form__number-input"
                                                :disabled="!row.enabled"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="issuesById[row.id]"
                                class="numbering-form__callout"
                                :class="`numbering-form__callout--${issuesById[row.id].type}`"
                            >
                                <exclamation-triangle
                                    v-if="issuesById[row.id].type === 'error'"
                                    :width="24"
                                    :height="24"
                                    fill="#455A64"
                                />
                                <exclamation-mark v-else :width="24" :height="24" fill="#455A64" />
                                <p>{{ issuesById[row.id].text }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 col-lg-4">
                <p class="form-input__hint">
                    Możesz podzielić pulę numerów startowych pomiędzy klasyfikacje. Wybierz predefiniowaną metodę
                    dzielenia lub ustaw własne parametry.
                </p>
            </div>
        </panel-competition-form-box>

        <div class="numbering-form__actions mat24 mab24">
            <base-button
                btn1
                :loading="assigning"
                :disabled="saving || assigning || hasBlockingErrors"
                @click="onAssign"
            >
                Nadaj numery i zapisz
            </base-button>
            <base-button btn2 :loading="saving" :disabled="saving || assigning || hasBlockingErrors" @click="onSave">
                Zapisz
            </base-button>
        </div>
    </div>
</template>

<script>
    import PanelCompetitionFormBox from '@/components/PanelCompetitionFormBox';
    import PanelInputBaseRadio from '@/components/PanelInputBaseRadio';
    import PanelInputCheckbox from '@/components/PanelInputCheckbox';
    import ExclamationMark from '@/components/icons/ExclamationMark.vue';
    import ExclamationTriangle from '@/components/icons/ExclamationTriangle.vue';

    const PRESETS = [
        { id: 'novalues', label: 'Bez podziału' },
        { id: 'uselimits', label: 'Wg limitów' },
        { id: 'every100', label: 'Pule po 100' },
        { id: 'every500', label: 'Pule po 500' },
    ];

    export default {
        name: 'PanelCompetitionNumberingForm',
        components: {
            PanelCompetitionFormBox,
            PanelInputBaseRadio,
            PanelInputCheckbox,
            ExclamationMark,
            ExclamationTriangle,
        },
        props: {
            value: {
                type: Object,
                required: true,
            },
            competition: {
                type: Object,
                required: true,
            },
            saving: {
                type: Boolean,
                default: false,
            },
            assigning: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                presets: PRESETS,
                activePreset: null,
            };
        },
        computed: {
            form: {
                get() {
                    return this.value;
                },
                set(next) {
                    this.$emit('input', next);
                },
            },
            hasCycle() {
                return Boolean(this.competition && this.competition.cycle);
            },
            isAuto() {
                return this.form.autoNumbers === 'auto';
            },
            issuesById() {
                const map = {};
                (this.form.classifications || []).forEach((row) => {
                    map[row.id] = this.rowIssue(row);
                });
                return map;
            },
            hasBlockingErrors() {
                return Object.keys(this.issuesById).some((id) => {
                    const issue = this.issuesById[id];
                    return issue && issue.type === 'error';
                });
            },
        },
        methods: {
            replaceClassifications(classifications) {
                this.form = { ...this.form, classifications };
            },
            toggleClassification(index, enabled) {
                const row = (this.form.classifications || [])[index];
                if (!row) {
                    return;
                }
                const next = { ...row, enabled };
                if (enabled && Number(row.numbersMin) === 0 && Number(row.numbersMax) === 0) {
                    next.numbersMin = 1;
                    next.numbersMax = 9999;
                }
                const classifications = this.form.classifications.slice();
                classifications.splice(index, 1, next);
                this.replaceClassifications(classifications);
            },
            rowRange(row) {
                return {
                    min: Number(row.numbersMin) || 0,
                    max: Number(row.numbersMax) || 0,
                };
            },
            isInvalidRange(row) {
                if (!row.enabled) {
                    return false;
                }
                const { min, max } = this.rowRange(row);
                return max < min;
            },
            hasPartialOverlap(row) {
                if (!row.enabled || this.isInvalidRange(row)) {
                    return false;
                }
                const a = this.rowRange(row);
                return (this.form.classifications || []).some((other) => {
                    if (other.id === row.id || !other.enabled || this.isInvalidRange(other)) {
                        return false;
                    }
                    const b = this.rowRange(other);
                    if (a.min === b.min && a.max === b.max) {
                        return false;
                    }
                    return a.min <= b.max && b.min <= a.max;
                });
            },
            poolSize(row) {
                const { min, max } = this.rowRange(row);
                if (!row.enabled || max < min) {
                    return 0;
                }
                return max - min + 1;
            },
            poolNeeded(row) {
                if (this.form.numbersOnlyPayed === 'paid') {
                    return Number(row.playersPayCount) || Number(row.playersTotal) || 0;
                }
                return Number(row.playersTotal) || 0;
            },
            poolWarning(row) {
                if (!row.enabled || this.isInvalidRange(row) || this.hasPartialOverlap(row)) {
                    return false;
                }
                const needed = this.poolNeeded(row);
                if (needed <= 0) {
                    return false;
                }
                return this.poolSize(row) < needed;
            },
            rowIssue(row) {
                if (!row.enabled) {
                    return null;
                }
                if (this.isInvalidRange(row)) {
                    return { type: 'error', text: 'Numer Od nie może być większy niż Do' };
                }
                if (this.hasPartialOverlap(row)) {
                    return { type: 'error', text: 'Zakres numerów nachodzi na inną klasyfikację' };
                }
                if (this.poolWarning(row)) {
                    const text =
                        this.form.numbersOnlyPayed === 'paid'
                            ? 'Za mała liczba numerów dla opłaconych uczestników'
                            : 'Za mała liczba numerów dla zarejestrowanych uczestników';
                    return { type: 'warning', text };
                }
                return null;
            },
            applyPreset(type) {
                this.activePreset = type;
                let cursor = 0;
                const classifications = (this.form.classifications || []).map((row) => {
                    const next = { ...row, enabled: true };
                    if (type === 'novalues') {
                        next.numbersMin = 1;
                        next.numbersMax = 9999;
                        return next;
                    }
                    let size = 100;
                    if (type === 'every500') {
                        size = 500;
                    } else if (type === 'uselimits') {
                        const limit = Number(row.playersLimit) || 0;
                        size = limit > 0 ? limit : Math.max(Number(row.playersTotal) || 1, 1);
                    }
                    next.numbersMin = cursor + 1;
                    next.numbersMax = cursor + size;
                    cursor += size;
                    return next;
                });
                this.replaceClassifications(classifications);
            },
            onSave() {
                if (this.hasBlockingErrors) {
                    return;
                }
                this.$emit('save');
            },
            onAssign() {
                if (this.hasBlockingErrors) {
                    return;
                }
                this.$emit('assign');
            },
        },
    };
</script>

<style scoped>
    .numbering-form {
        width: 100%;
    }
    .numbering-form .form.numbering-form__box {
        padding: 24px 16px;
        overflow: visible;
    }
    .form-input__label {
        margin: 0 0 16px;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: var(--color-white);
    }
    .form-input__hint {
        margin-top: 16px;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: var(--color-white);
    }
    .numbering-form__nested {
        margin: 8px 0 0 7px;
        padding-left: 16px;
        border-left: 1px solid #78909c;
        min-height: 32px;
    }
    .numbering-form__nested--disabled {
        opacity: 0.5;
        pointer-events: none;
    }
    .numbering-form__nested >>> .panel-radio-base-field + .panel-radio-base-field {
        margin-top: 8px;
    }
    .numbering-form__params-main {
        overflow: visible;
    }
    .numbering-form__params-main > .form-input__label {
        margin-bottom: 0;
    }
    .numbering-form__params-body {
        display: flex;
        flex-direction: column;
        gap: 24px;
        margin-top: 24px;
    }
    .numbering-form__presets-block {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .numbering-form__preset-label {
        margin: 0;
        font-size: 12px;
        font-weight: 400;
        line-height: 20px;
        color: var(--color-white);
    }
    .numbering-form__presets {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .numbering-form .numbering-form__preset.btn {
        flex: 1 1 0;
        min-width: 0;
        height: 40px;
        padding: 0 8px;
        background: transparent;
        background-image: none;
        border: 1px solid #78909c;
        box-shadow: none;
        color: #fff;
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
    }
    .numbering-form .numbering-form__preset--active.btn {
        background-color: rgba(255, 255, 255, 0.12);
        border-color: #fff;
    }
    .numbering-form__table {
        width: 100%;
    }
    .numbering-form__table-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
        padding: 8px 0;
        border-bottom: 1px solid #78909c;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: var(--color-white);
    }
    .numbering-form__col-name-head {
        min-width: 0;
    }
    .numbering-form__col-ranges {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        gap: 8px;
        margin-left: auto;
    }
    .numbering-form__col-from,
    .numbering-form__col-to {
        width: 68px;
        flex-shrink: 0;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: var(--color-white);
    }
    .numbering-form__col-sep {
        width: 16px;
        flex-shrink: 0;
        text-align: center;
        font-size: 14px;
        line-height: 20px;
        color: var(--color-white);
    }
    .numbering-form__col-sep--head {
        visibility: hidden;
    }
    .numbering-form__row {
        position: relative;
    }
    .numbering-form__row-main {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 0;
        border-bottom: 1px solid #78909c;
    }
    .numbering-form__check {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        margin: 0;
        cursor: pointer;
    }
    .numbering-form__check input {
        position: absolute;
        width: 0;
        height: 0;
        opacity: 0;
        pointer-events: none;
    }
    .numbering-form__check-box {
        box-sizing: border-box;
        width: 18px;
        height: 18px;
        background: #fff;
        border-radius: 2px;
        position: relative;
    }
    .numbering-form__check input:checked + .numbering-form__check-box::after {
        content: '';
        position: absolute;
        left: 5px;
        top: 2px;
        width: 5px;
        height: 9px;
        border: solid #ff3d00;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
    }
    .numbering-form__col-body {
        display: flex;
        flex: 1;
        align-items: flex-start;
        gap: 16px;
        min-width: 0;
    }
    .numbering-form__col-name {
        flex: 1;
        min-width: 0;
    }
    .numbering-form__cls-name {
        margin: 0;
        color: var(--color-white);
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
    }
    .numbering-form__cls-meta {
        display: flex;
        align-items: center;
        gap: 4px;
        margin: 2px 0 0;
        color: var(--color-white);
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
    }
    .numbering-form__cls-meta i {
        width: 16px;
        font-size: 12px;
        text-align: center;
    }
    .numbering-form__col-limit {
        width: 133px;
        flex-shrink: 0;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: var(--color-white);
    }
    .numbering-form__col-limit p {
        margin: 0;
    }
    .numbering-form__number-input {
        box-sizing: border-box;
        width: 68px;
        height: 40px;
        border: 0;
        border-radius: 4px;
        padding: 10px 12px;
        text-align: left;
        background: #fff;
        color: #455a64;
        font-size: 14px;
        line-height: 20px;
        box-shadow: 0 8px 6px rgba(0, 0, 0, 0.1);
    }
    .numbering-form__number-input::-webkit-outer-spin-button,
    .numbering-form__number-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        appearance: none;
        margin: 0;
    }
    .numbering-form__number-input[type='number'] {
        -moz-appearance: textfield;
        appearance: textfield;
    }
    .numbering-form__number-input:disabled {
        opacity: 0.5;
    }
    .numbering-form__row--disabled .numbering-form__cls-name,
    .numbering-form__row--disabled .numbering-form__cls-meta,
    .numbering-form__row--disabled .numbering-form__col-limit {
        opacity: 0.55;
    }
    .numbering-form__callout {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        box-sizing: border-box;
        width: 100%;
        max-width: 236px;
        min-height: 40px;
        margin: 8px 0 8px 40px;
        padding: 4px 8px 4px 10px;
        border-radius: 4px;
        position: relative;
        color: #455a64;
    }
    .numbering-form__callout::before {
        content: '';
        position: absolute;
        left: -6px;
        top: 50%;
        width: 12px;
        height: 12px;
        transform: translateY(-50%) rotate(45deg);
        background: inherit;
        border-radius: 1px;
    }
    .numbering-form__callout--warning {
        background: #f6e7c8;
    }
    .numbering-form__callout--error {
        background: #f6d0d0;
    }
    .numbering-form__callout p {
        margin: 0;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        color: #455a64;
    }
    .numbering-form__callout svg {
        flex-shrink: 0;
        margin-top: 0;
    }
    .numbering-form__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        justify-content: space-between;
        align-items: center;
    }
    @media (min-width: 992px) {
        .numbering-form .form.numbering-form__box {
            padding: 24px 32px;
        }
        .form-input__hint {
            margin-left: 15px;
            margin-top: 0;
        }
        .numbering-form__box--params {
            overflow: visible;
        }
        .numbering-form__callout {
            position: absolute;
            left: calc(100% + 32px);
            top: 8px;
            width: 236px;
            max-width: 236px;
            margin: 0;
            z-index: 2;
        }
        .numbering-form__actions {
            margin-left: 0;
        }
    }
    @media (max-width: 575px) {
        .numbering-form__col-body {
            flex-wrap: wrap;
        }
        .numbering-form__col-limit {
            width: 100%;
        }
        .numbering-form .numbering-form__preset.btn {
            flex: 1 1 calc(50% - 8px);
        }
    }
</style>
